<script setup lang="ts">
import { CalendarDate, getLocalTimeZone, today, type DateValue } from '@internationalized/date';
import { Calendar, AlertCircle } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import type { AcceptableValue } from 'reka-ui';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RangeCalendar } from '@/components/ui/range-calendar';
import { useDateRangeStore } from '@/stores/date-range';
import { storeToRefs } from 'pinia';
import { toast } from 'vue-sonner';

const dateRangeStore = useDateRangeStore();
const { selectedDateRange, formattedDateRange } = storeToRefs(dateRangeStore);

// Calendar picker state
const isCalendarOpen = ref(false);

// Track the user's explicit selection (what button they actually clicked)
// This is needed because "7 Days" and "This Week" might have the same date range
const lastExplicitSelection = ref<string | undefined>(undefined);

// Maximum allowed date range in days
const MAX_RANGE_DAYS = 93;

// Preset options
const presetOptions = [
  { value: 'today', label: 'Today', days: 1 },
  { value: '7d', label: '7 Days', days: 7 },
  { value: '14d', label: '14 Days', days: 14 },
  { value: '28d', label: '28 Days', days: 28 },
];

const periodOptions = [
  { value: 'this-week', label: 'This Week' },
  { value: 'last-week', label: 'Last Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
];

// Helper functions for period calculations
function getStartOfWeek(date: CalendarDate): CalendarDate {
  const jsDate = new Date(date.year, date.month - 1, date.day);
  const dayOfWeek = jsDate.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as start of week
  jsDate.setDate(jsDate.getDate() - diff);
  return new CalendarDate(jsDate.getFullYear(), jsDate.getMonth() + 1, jsDate.getDate());
}

function getEndOfWeek(date: CalendarDate): CalendarDate {
  const start = getStartOfWeek(date);
  return start.add({ days: 6 });
}

function getStartOfMonth(date: CalendarDate): CalendarDate {
  return new CalendarDate(date.year, date.month, 1);
}

function getEndOfMonth(date: CalendarDate): CalendarDate {
  const nextMonth =
    date.month === 12
      ? new CalendarDate(date.year + 1, 1, 1)
      : new CalendarDate(date.year, date.month + 1, 1);
  return nextMonth.subtract({ days: 1 });
}

// Detect current selection type
const currentSelection = computed(() => {
  const { start, end } = selectedDateRange.value;
  const todayValue = today(getLocalTimeZone());

  // Helper function to check if a selection matches current date range
  const matchesRange = (selectionValue: string) => {
    // Check presets
    const preset = presetOptions.find(p => p.value === selectionValue);
    if (preset) {
      const presetStart = todayValue.subtract({ days: preset.days - 1 });
      return start.compare(presetStart) === 0 && end.compare(todayValue) === 0;
    }

    // Check periods
    switch (selectionValue) {
      case 'this-week': {
        const thisWeekStart = getStartOfWeek(todayValue);
        const thisWeekEnd = getEndOfWeek(todayValue);
        return start.compare(thisWeekStart) === 0 && end.compare(thisWeekEnd) === 0;
      }
      case 'last-week': {
        const thisWeekStart = getStartOfWeek(todayValue);
        const lastWeekStart = thisWeekStart.subtract({ days: 7 });
        const lastWeekEnd = thisWeekStart.subtract({ days: 1 });
        return start.compare(lastWeekStart) === 0 && end.compare(lastWeekEnd) === 0;
      }
      case 'this-month': {
        const thisMonthStart = getStartOfMonth(todayValue);
        const thisMonthEnd = getEndOfMonth(todayValue);
        return start.compare(thisMonthStart) === 0 && end.compare(thisMonthEnd) === 0;
      }
      case 'last-month': {
        const lastMonthDate = todayValue.subtract({ months: 1 });
        const lastMonthStart = getStartOfMonth(lastMonthDate);
        const lastMonthEnd = getEndOfMonth(lastMonthDate);
        return start.compare(lastMonthStart) === 0 && end.compare(lastMonthEnd) === 0;
      }
    }
    return false;
  };

  // If we have an explicit selection and it still matches, use it
  if (lastExplicitSelection.value && matchesRange(lastExplicitSelection.value)) {
    return lastExplicitSelection.value;
  }

  // Otherwise, compute from date range (check presets first, then periods)
  // Check if it matches any preset (X days ending today)
  for (const preset of presetOptions) {
    const presetStart = todayValue.subtract({ days: preset.days - 1 });
    if (start.compare(presetStart) === 0 && end.compare(todayValue) === 0) {
      return preset.value;
    }
  }

  // Check if it matches any period
  const thisWeekStart = getStartOfWeek(todayValue);
  const thisWeekEnd = getEndOfWeek(todayValue);
  if (start.compare(thisWeekStart) === 0 && end.compare(thisWeekEnd) === 0) {
    return 'this-week';
  }

  const lastWeekStart = thisWeekStart.subtract({ days: 7 });
  const lastWeekEnd = thisWeekEnd.subtract({ days: 7 });
  if (start.compare(lastWeekStart) === 0 && end.compare(lastWeekEnd) === 0) {
    return 'last-week';
  }

  const thisMonthStart = getStartOfMonth(todayValue);
  const thisMonthEnd = getEndOfMonth(todayValue);
  if (start.compare(thisMonthStart) === 0 && end.compare(thisMonthEnd) === 0) {
    return 'this-month';
  }

  const lastMonthDate = todayValue.subtract({ months: 1 });
  const lastMonthStart = getStartOfMonth(lastMonthDate);
  const lastMonthEnd = getEndOfMonth(lastMonthDate);
  if (start.compare(lastMonthStart) === 0 && end.compare(lastMonthEnd) === 0) {
    return 'last-month';
  }

  // Return undefined for custom ranges (not in dropdown)
  // This prevents Select from being in invalid state
  return undefined;
});

// Handle preset/period selection
function handleSelectionChange(value: AcceptableValue) {
  if (!value || typeof value !== 'string') {return;}
  const todayValue = today(getLocalTimeZone());

  // Store the explicit selection before changing the date range
  lastExplicitSelection.value = value;

  // Handle preset days
  const preset = presetOptions.find(p => p.value === value);
  if (preset) {
    const start = todayValue.subtract({ days: preset.days - 1 });
    dateRangeStore.setDateRange(start, todayValue);
    return;
  }

  // Handle periods
  switch (value) {
    case 'this-week': {
      const start = getStartOfWeek(todayValue);
      const end = getEndOfWeek(todayValue);
      dateRangeStore.setDateRange(start, end);
      break;
    }
    case 'last-week': {
      const thisWeekStart = getStartOfWeek(todayValue);
      const start = thisWeekStart.subtract({ days: 7 });
      const end = thisWeekStart.subtract({ days: 1 });
      dateRangeStore.setDateRange(start, end);
      break;
    }
    case 'this-month': {
      const start = getStartOfMonth(todayValue);
      const end = getEndOfMonth(todayValue);
      dateRangeStore.setDateRange(start, end);
      break;
    }
    case 'last-month': {
      const lastMonthDate = todayValue.subtract({ months: 1 });
      const start = getStartOfMonth(lastMonthDate);
      const end = getEndOfMonth(lastMonthDate);
      dateRangeStore.setDateRange(start, end);
      break;
    }
  }
}

// Calculate days between two dates
function calculateDaysDifference(start: CalendarDate, end: CalendarDate): number {
  const startDate = new Date(start.year, start.month - 1, start.day);
  const endDate = new Date(end.year, end.month - 1, end.day);
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1; // Include both start and end dates
}

// Disable dates that would create a range > MAX_RANGE_DAYS
function isDateDisabled(date: DateValue): boolean {
  const currentRange = selectedDateRange.value;

  // Convert DateValue to CalendarDate for comparison
  const calendarDate = new CalendarDate(date.year, date.month, date.day);

  // If we have a start date but no end date (user is selecting end date)
  if (currentRange.start && !currentRange.end) {
    const daysDiff = calculateDaysDifference(currentRange.start, calendarDate);
    return daysDiff > MAX_RANGE_DAYS;
  }

  return false;
}

// Handle calendar date range change
function handleCalendarChange(newRange: any) {
  if (newRange && newRange.start && newRange.end) {
    // Validate range is not longer than MAX_RANGE_DAYS
    const daysDiff = calculateDaysDifference(newRange.start, newRange.end);

    if (daysDiff > MAX_RANGE_DAYS) {
      toast({
        title: 'Date range too long',
        description: `Please select a range of ${MAX_RANGE_DAYS} days or less. Selected range: ${daysDiff} days.`,
        variant: 'destructive',
      });
      return;
    }

    // Clear explicit selection when using calendar picker
    // This allows computed matching to determine the best button to highlight
    lastExplicitSelection.value = undefined;

    dateRangeStore.setDateRange(newRange.start, newRange.end);
    isCalendarOpen.value = false;
  }
}

// Display value for select
const displayValue = computed(() => {
  // If no preset/period matches (undefined), show formatted date range
  if (!currentSelection.value) {
    return formattedDateRange.value;
  }

  const preset = presetOptions.find(p => p.value === currentSelection.value);
  if (preset) {return preset.label;}

  const period = periodOptions.find(p => p.value === currentSelection.value);
  if (period) {return period.label;}

  // Fallback to formatted date range
  return formattedDateRange.value;
});
</script>

<template>
  <div class="flex items-center gap-2">
    <!-- Unified Date Range Picker -->
    <Popover v-model:open="isCalendarOpen">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          class="h-8 w-auto min-w-[180px] justify-between px-3"
          title="Select date range"
        >
          <span>{{ displayValue }}</span>
          <Calendar class="ml-2 h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-auto p-0" align="start">
        <div class="flex flex-col">
          <!-- Top: Presets in 2 columns -->
          <div class="grid grid-cols-2 gap-x-4 gap-y-0.5 border-b p-3">
            <!-- Left Column: Calendar Periods -->
            <div class="flex flex-col gap-0.5">
              <h3 class="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Periods
              </h3>
              <Button
                v-for="period in periodOptions"
                :key="period.value"
                variant="ghost"
                class="h-8 w-full justify-start px-2 text-sm font-normal"
                :class="{ 'bg-accent': currentSelection === period.value }"
                @click="handleSelectionChange(period.value)"
              >
                {{ period.label }}
              </Button>
            </div>

            <!-- Right Column: Relative Ranges -->
            <div class="flex flex-col gap-0.5">
              <h3 class="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Relative
              </h3>
              <Button
                v-for="preset in presetOptions"
                :key="preset.value"
                variant="ghost"
                class="h-8 w-full justify-start px-2 text-sm font-normal"
                :class="{ 'bg-accent': currentSelection === preset.value }"
                @click="handleSelectionChange(preset.value)"
              >
                {{ preset.label }}
              </Button>
            </div>
          </div>

          <!-- Bottom: Calendar -->
          <div class="flex flex-col items-center gap-2 p-2">
            <RangeCalendar
              :model-value="selectedDateRange"
              :number-of-months="1"
              :is-date-disabled="isDateDisabled"
              @update:model-value="handleCalendarChange"
            />
            <div class="flex items-center gap-1.5 text-xs text-muted-foreground px-3 pb-1">
              <AlertCircle class="h-3.5 w-3.5" />
              <span>Maximum range: {{ MAX_RANGE_DAYS }} days</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
